/**
 * Subscription-lifecycle banner (PR 6f).
 *
 * Renders one of three states based on entitlement metadata stamped by
 * the webhook (PR 6a):
 *
 *   - ``cancelAtPeriodEnd=true``  → "Your <plan> ends on <date>. You'll
 *                                    move to Pay-as-you-go." Re-subscribe
 *                                    button.
 *   - ``pendingPlanKey``         → "Switching to <plan> on <renewal>"
 *                                    (end-of-period downgrade scheduled).
 *                                    Manage button.
 *   - ``billingState='past_due'`` → "Card declined; update payment
 *                                    method." Manage button.
 *
 * Mounted ABOVE BillingBudgetBanner. Lifecycle events (cancel /
 * downgrade scheduling / payment failure) are higher-priority than
 * usage warnings — a user about to lose Pro access at period end
 * should see that BEFORE a "you're at 60% usage" banner.
 *
 * Dismissal:
 *   * Cancel-at-period-end is dismissible — it sticks until the period
 *     actually ends (then ``customer.subscription.deleted`` fires and
 *     the banner becomes the past_due / payg-fallback shape via the
 *     PR 6a deleted handler).
 *   * Pending downgrade is dismissible — same logic; the row becomes
 *     active-on-the-new-plan after renewal.
 *   * past_due is NOT dismissible — the user must update their payment
 *     method (or the next dunning attempt fixes it).
 *
 * Dismissal key: (orgId, state, periodEndUnix or pendingEffective).
 * A re-cancel after the period flips re-shows; a fresh past_due after a
 * recovery re-shows.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { AlertTriangle, CalendarClock, X } from 'lucide-react';
import { useAuth } from '@stepflow/ui-shell/context/AuthContext.jsx';
import { fetchOrgBillingEntitlement, isBillingUiEnabled } from '@/services/stripeBilling';
import { useTabFocusRefetch } from '@stepflow/ui-shared/hooks/useTabFocusRefetch.js';
import { GENERATED_PLAN_CATALOG } from '@stepflow/ui-shared/services/generatedPricing';
import ManageSubscriptionButton from './ManageSubscriptionButton.jsx';
import {
  normalizeStepflowRole,
  canAccessWorkspaceAdmin,
} from '@/utils/stepflowRoles';

function canViewOrgBilling(user, workspaceRole) {
  const stepflow = normalizeStepflowRole(user?.stepflowRole);
  if (stepflow === 'OWNER') return true;
  return canAccessWorkspaceAdmin(workspaceRole);
}

const DISMISS_KEY = 'stepflowSubscriptionLifecycleBannerDismiss';

function readDismissedKey() {
  try {
    return sessionStorage.getItem(DISMISS_KEY) || '';
  } catch {
    return '';
  }
}

function planNameFor(planKey) {
  const k = (planKey || '').toLowerCase();
  const p = GENERATED_PLAN_CATALOG.find((x) => x.key === k);
  return p?.name || planKey || 'your plan';
}

function formatDate(unix) {
  if (!unix) return null;
  try {
    return new Date(unix * 1000).toLocaleDateString(undefined, {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  } catch {
    return null;
  }
}

/**
 * Resolve which lifecycle state to render. Order of precedence:
 *   1. past_due — payment problem must be addressed before anything else.
 *   2. cancel_at_period_end — user explicitly chose to leave; respect it.
 *   3. pending downgrade — softest signal of the three.
 * Returns null when none apply (steady state).
 */
function resolveLifecycleState(data) {
  if (!data) return null;
  if (data.billingState === 'past_due') return 'past_due';
  if (data.cancelAtPeriodEnd) return 'cancel_at_period_end';
  if (data.pendingPlanKey) return 'pending_downgrade';
  return null;
}

function dismissalKey(state, data) {
  const oid = data?.orgId || 'noorg';
  const peg = data?.pendingPlanEffectiveAtUnix || data?.periodEndUnix || 0;
  return `${oid}|${state}|${peg}`;
}

const PALETTES = {
  past_due: {
    wrap: 'border-rose-200/90 bg-gradient-to-r from-rose-50 to-rose-50/50',
    iconWrap: 'bg-rose-100 text-rose-700',
    icon: AlertTriangle,
    title: 'text-rose-950',
    body: 'text-rose-900/90',
    dismissible: false,
  },
  cancel_at_period_end: {
    wrap: 'border-slate-200/90 bg-gradient-to-r from-slate-50 to-slate-50/50',
    iconWrap: 'bg-slate-100 text-slate-700',
    icon: CalendarClock,
    title: 'text-slate-900',
    body: 'text-slate-700',
    dismissible: true,
  },
  pending_downgrade: {
    wrap: 'border-sky-200/90 bg-gradient-to-r from-sky-50 to-sky-50/50',
    iconWrap: 'bg-sky-100 text-sky-700',
    icon: CalendarClock,
    title: 'text-sky-950',
    body: 'text-sky-900/90',
    dismissible: true,
  },
};

export default function SubscriptionLifecycleBanner() {
  const { user, workspaceRole } = useAuth();
  // Billing UI master switch (single-account/no-Stripe deploys set it false).
  const allowed = isBillingUiEnabled() && canViewOrgBilling(user, workspaceRole);

  const [data, setData] = useState(null);
  const [dismissedFor, setDismissedFor] = useState(readDismissedKey);

  const refresh = useCallback(async () => {
    if (!allowed) {
      setData(null);
      return;
    }
    try {
      const d = await fetchOrgBillingEntitlement();
      setData(d);
    } catch {
      setData(null);
    }
  }, [allowed]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (cancelled) return;
      await refresh();
    })();
    return () => {
      cancelled = true;
    };
  }, [refresh]);

  // Re-fetch when user returns from Stripe Portal so a freshly-clicked
  // Cancel surfaces the banner immediately (the webhook may have landed
  // by the time the tab regains focus).
  useTabFocusRefetch(refresh, allowed);

  if (!allowed) return null;
  if (!data?.hasEntitlement) return null;
  const state = resolveLifecycleState(data);
  if (!state) return null;

  const cfg = PALETTES[state];
  const dkey = dismissalKey(state, data);
  if (cfg.dismissible && dismissedFor === dkey) return null;

  const Icon = cfg.icon;

  let title = '';
  let body = '';
  const planName = planNameFor(data.planKey);
  const endDate = formatDate(data.periodEndUnix);
  const pendingDate = formatDate(data.pendingPlanEffectiveAtUnix);
  const pendingName = planNameFor(data.pendingPlanKey);

  if (state === 'past_due') {
    title = 'Payment failed — please update your payment method';
    body =
      "Your latest invoice didn't go through. We'll retry automatically, but updating your card now avoids any interruption to runs.";
  } else if (state === 'cancel_at_period_end') {
    title = endDate
      ? `Your ${planName} ends on ${endDate}`
      : `Your ${planName} subscription is ending`;
    body =
      "After that you'll move to Pay-as-you-go with a $0 wallet — top up anytime to keep running, or re-subscribe to restore your monthly budget.";
  } else if (state === 'pending_downgrade') {
    title = pendingDate
      ? `Switching to ${pendingName} on ${pendingDate}`
      : `Switching to ${pendingName} at your next renewal`;
    body = `Your ${planName} budget stays in effect until then — no change to current runs.`;
  }

  const onDismiss = () => {
    if (!cfg.dismissible) return;
    try {
      sessionStorage.setItem(DISMISS_KEY, dkey);
    } catch {
      /* ignore */
    }
    setDismissedFor(dkey);
  };

  return (
    <div
      className={`flex shrink-0 items-start gap-3 border-b px-4 py-3 sm:items-center sm:px-5 ${cfg.wrap}`}
      role="region"
      aria-label="Subscription status"
      data-testid={`subscription-lifecycle-banner-${state}`}
    >
      <div
        className={`mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-lg ${cfg.iconWrap}`}
      >
        <Icon className="h-5 w-5" aria-hidden />
      </div>
      <div className="min-w-0 flex-1">
        <p className={`text-sm font-medium ${cfg.title}`}>{title}</p>
        <p className={`mt-0.5 text-xs ${cfg.body}`}>{body}</p>
        <div className="mt-2 flex flex-wrap items-center gap-2">
          <ManageSubscriptionButton allowed={allowed} returnPath="/billing" />
        </div>
      </div>
      {cfg.dismissible ? (
        <button
          type="button"
          onClick={onDismiss}
          aria-label="Dismiss subscription notice"
          className="ml-2 inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-md text-slate-500 transition hover:bg-white/60 hover:text-slate-800"
        >
          <X className="h-4 w-4" aria-hidden />
        </button>
      ) : null}
    </div>
  );
}
